import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Circle, User, Package, ClipboardList, Rocket, ArrowRight, Sparkles } from "lucide-react";

export default function OnboardingWizard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState(null);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [techForm, setTechForm] = useState({ name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user || user.role !== "admin" || user.is_super_admin) return;
    api.get("/onboarding/state").then((r) => {
      setState(r.data);
      if (!r.data.completed) setOpen(true);
    }).catch(() => {});
  }, [user]);

  if (!open || !state) return null;

  const addTechnician = async () => {
    if (!techForm.name || !techForm.email || !techForm.password) return toast.error(t("auto.compila_tutti_i_campi"));
    setSaving(true);
    try {
      await api.post("/users", {
        name: techForm.name,
        email: techForm.email,
        password: techForm.password,
        role: "tecnico",
      });
      toast.success(t("auto.tecnico_aggiunto"));
      setTechForm({ name: "", email: "", password: "" });
      const s = await api.get("/onboarding/state");
      setState(s.data);
      setStep(2);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const finish = async () => {
    try {
      await api.post("/onboarding/complete");
      setOpen(false);
      toast.success("Setup completato! Buon lavoro 🎉");
    } catch (_) { setOpen(false); }
  };

  const skip = async () => {
    await api.post("/onboarding/complete").catch(() => {});
    setOpen(false);
  };

  const goTo = (path) => { finish(); setTimeout(() => nav(path), 100); };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) skip(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Sparkles className="h-6 w-6 text-[#0052FF]" /> Benvenuto in EVOSOFT!
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Ci vogliono <b>meno di 5 minuti</b> per iniziare. Seguiamo insieme i primi passi:
          </p>

          {/* Progress steps */}
          <div className="flex items-center justify-between rounded-sm border border-slate-200 bg-slate-50 p-3">
            {[
              { n: 1, label: "Setup base", done: true, icon: Rocket },
              { n: 2, label: "Primo tecnico", done: state.steps.add_technician, icon: User },
              { n: 3, label: "Materiali & listino", done: state.steps.materials_ready && state.steps.listino_ready, icon: Package },
            ].map((s, i) => (
              <div key={i} className="flex flex-1 items-center">
                <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${s.done ? "bg-emerald-500 text-white" : step === s.n ? "bg-[#0052FF] text-white" : "bg-slate-200 text-slate-400"}`}>
                  {s.done ? <CheckCircle2 className="h-5 w-5" /> : <s.icon className="h-4 w-4" />}
                </div>
                <div className="ml-2 text-xs font-semibold text-slate-700">{s.label}</div>
                {i < 2 && <div className={`mx-2 h-0.5 flex-1 ${s.done ? "bg-emerald-500" : "bg-slate-200"}`} />}
              </div>
            ))}
          </div>

          {/* Step 1: Welcome */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="rounded-sm border border-blue-200 bg-blue-50 p-4">
                <div className="flex items-start gap-3">
                  <Rocket className="mt-0.5 h-5 w-5 text-[#0052FF]" />
                  <div>
                    <div className="font-semibold text-slate-900">La tua azienda è configurata ✅</div>
                    <div className="mt-1 text-sm text-slate-600">Abbiamo già preparato per te <b>materiali di consumo</b>, <b>{t("auto.committenti_demo")}</b> (FiberCop, OpenFiber) e un <b>listino base</b>. Puoi personalizzarli in qualsiasi momento.</div>
                  </div>
                </div>
              </div>
              <Button onClick={() => setStep(2)} className="w-full rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="onb-next-1">
                Aggiungiamo il primo tecnico <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          )}

          {/* Step 2: Add technician */}
          {step === 2 && (
            <div className="space-y-4">
              {state.steps.add_technician ? (
                <div className="rounded-sm border border-emerald-200 bg-emerald-50 p-4">
                  <div className="flex items-center gap-2 text-emerald-900">
                    <CheckCircle2 className="h-5 w-5" />
                    <div><b>{state.counts.technicians} tecnic{state.counts.technicians === 1 ? "o" : "i"}</b> già aggiunt{state.counts.technicians === 1 ? "o" : "i"}. Puoi aggiungerne altri dal menu Tecnici.</div>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-sm text-slate-600">{t("auto.aggiungi_il_tuo_primo_tecnico_riceverà_le_credenziali_per_ac")}</p>
                  <div className="space-y-3">
                    <div><Label>Nome tecnico *</Label><Input value={techForm.name} onChange={(e) => setTechForm({ ...techForm, name: e.target.value })} placeholder="Es. Mario Rossi" className="rounded-sm" data-testid="onb-tech-name" /></div>
                    <div><Label>Email *</Label><Input type="email" value={techForm.email} onChange={(e) => setTechForm({ ...techForm, email: e.target.value })} placeholder="mario@..." className="rounded-sm font-mono" data-testid="onb-tech-email" /></div>
                    <div><Label>Password iniziale *</Label><Input type="password" value={techForm.password} onChange={(e) => setTechForm({ ...techForm, password: e.target.value })} placeholder="Almeno 6 caratteri" className="rounded-sm font-mono" data-testid="onb-tech-password" /></div>
                  </div>
                  <Button onClick={addTechnician} disabled={saving} className="w-full rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="onb-tech-add">
                    {saving ? "Aggiunta..." : "Aggiungi e continua"}
                  </Button>
                </>
              )}
              <div className="flex justify-between">
                <Button variant="ghost" onClick={() => setStep(1)}>← Indietro</Button>
                <Button variant="outline" onClick={() => setStep(3)} data-testid="onb-skip-2">
                  Salta →
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Materials/Listini */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="rounded-sm border border-emerald-200 bg-emerald-50 p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-600" />
                  <div>
                    <div className="font-semibold text-slate-900">{t("auto.tutto_pronto_per_iniziare")}</div>
                    <div className="mt-1 text-sm text-slate-600">
                      <b>{state.counts.materials}</b> materiali · <b>{state.counts.committenti}</b> committenti · <b>{state.counts.listini}</b> listin{state.counts.listini === 1 ? "o" : "i"}
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-sm text-slate-600">Da cosa vuoi partire?</p>
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                <button onClick={() => goTo("/magazzino")} className="rounded-sm border border-slate-200 p-3 text-left hover:border-[#0052FF] hover:bg-blue-50" data-testid="onb-go-magazzino">
                  <div className="flex items-center gap-2"><Package className="h-4 w-4 text-[#0052FF]" /> <b>{t("auto.carica_il_magazzino")}</b></div>
                  <div className="mt-1 text-xs text-slate-500">{t("auto.aggiungi_seriali_via_barcode_o_excel")}</div>
                </button>
                <button onClick={() => goTo("/assegnazioni")} className="rounded-sm border border-slate-200 p-3 text-left hover:border-[#0052FF] hover:bg-blue-50" data-testid="onb-go-assegnazioni">
                  <div className="flex items-center gap-2"><ClipboardList className="h-4 w-4 text-[#0052FF]" /> <b>{t("auto.assegna_appuntamenti")}</b></div>
                  <div className="mt-1 text-xs text-slate-500">{t("auto.carica_il_file_excel_della_pianifica")}</div>
                </button>
                <button onClick={() => goTo("/listini")} className="rounded-sm border border-slate-200 p-3 text-left hover:border-[#0052FF] hover:bg-blue-50" data-testid="onb-go-listini">
                  <div className="flex items-center gap-2"><ClipboardList className="h-4 w-4 text-[#0052FF]" /> <b>Personalizza listini</b></div>
                  <div className="mt-1 text-xs text-slate-500">{t("auto.modifica_prezzi_per_ogni_committente")}</div>
                </button>
                <button onClick={() => goTo("/tecnici")} className="rounded-sm border border-slate-200 p-3 text-left hover:border-[#0052FF] hover:bg-blue-50" data-testid="onb-go-tecnici">
                  <div className="flex items-center gap-2"><User className="h-4 w-4 text-[#0052FF]" /> <b>{t("auto.gestisci_tecnici")}</b></div>
                  <div className="mt-1 text-xs text-slate-500">{t("auto.aggiungi_utenti_e_gestisci_ruoli")}</div>
                </button>
              </div>
              <div className="flex justify-between">
                <Button variant="ghost" onClick={() => setStep(2)}>← Indietro</Button>
                <Button onClick={finish} className="rounded-sm bg-emerald-600 hover:bg-emerald-700" data-testid="onb-finish">
                  <CheckCircle2 className="mr-1 h-4 w-4" /> Concludi setup
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
